import { ButtonSecondary, Icon, MultiSelectOutlined, type OptionProps } from '@amelio/ui-library';

import {
    AdvancedOptionFilter,
    QuestionnaireFilters,
    type AdvancedOptionFilterType,
} from '@/constants/questionnaire/Filters';
import { type QuestionTypeValue } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getOptionLabelText } from '@/utils/optionLabel';
import { getQuestionTypeOptions } from '@/utils/question';

interface ToolbarProps {
    filterOptions: OptionProps[];
    selectedFilters: string[];
    advancedOptionsFilters: AdvancedOptionFilterType[];
    questionTypeFilters: QuestionTypeValue[];
    handleFilterChange: (filter: string) => void;
    onAddQuestionClick: () => void;
    onQuestionTypeFilterChange: (filter: QuestionTypeValue) => void;
    onAdvancedOptionsFilterChange: (filter: AdvancedOptionFilterType) => void;
    onTranslateClick?: () => void;
}

export const Toolbar = ({
    filterOptions,
    selectedFilters,
    advancedOptionsFilters,
    questionTypeFilters,
    handleFilterChange,
    onAddQuestionClick,
    onQuestionTypeFilterChange,
    onAdvancedOptionsFilterChange,
    onTranslateClick,
}: ToolbarProps) => {
    const { t } = useAmelioTranslations();

    const advancedOptionsItems: OptionProps[] = [
        {
            label: decodeAndSanitize(t('common.mandatory')),
            value: AdvancedOptionFilter.IS_ANSWER_MANDATORY,
        },
        {
            label: decodeAndSanitize(t('common.optional')),
            value: AdvancedOptionFilter.IS_NOT_ANSWER_MANDATORY,
        },
    ];

    return (
        <div className={'flex flex-col'}>
            <div className={'flex w-full flex-col justify-between md:flex-row'}>
                <div className={'mb-5 flex gap-4 md:mb-0'}>
                    <MultiSelectOutlined
                        className={'max-w-60 px-8'}
                        options={filterOptions.map((option) => ({
                            ...option,
                            label: decodeAndSanitize(getOptionLabelText(option.label)),
                        }))}
                        placeholder={t('common.filter')}
                        values={selectedFilters}
                        onChange={handleFilterChange}
                        chevronPosition={'left'}
                        size={'lg'}
                    />
                </div>

                <div className={'flex flex-row flex-wrap gap-4'}>
                    {onTranslateClick && (
                        <ButtonSecondary
                            onClick={onTranslateClick}
                            leftIcon={
                                <Icon
                                    name={'messages-message'}
                                    size={'lg'}
                                />
                            }
                        >
                            {t('common.translate')}
                        </ButtonSecondary>
                    )}

                    <ButtonSecondary
                        onClick={onAddQuestionClick}
                        leftIcon={
                            <Icon
                                name={'essential-add-circle'}
                                size={'lg'}
                            />
                        }
                        className={'!min-h-auto sm:!min-h-10'}
                    >
                        <span className={'max-w-[150px] whitespace-normal sm:max-w-none'}>
                            {t('evaluation360Questionnaire.toolbar.addQuestion')}
                        </span>
                    </ButtonSecondary>
                </div>
            </div>

            <div className={'mt-4 flex flex-col gap-4 md:flex-row'}>
                {selectedFilters.includes(QuestionnaireFilters.ADVANCED_OPTIONS) && (
                    <div className={'min-w-60 shrink-0'}>
                        <MultiSelectOutlined
                            options={advancedOptionsItems}
                            values={advancedOptionsFilters}
                            onChange={(value) =>
                                onAdvancedOptionsFilterChange(value as AdvancedOptionFilterType)
                            }
                            showSelection={true}
                            label={decodeAndSanitize(t('common.advancedOptions'))}
                        />
                    </div>
                )}

                {selectedFilters.includes(QuestionnaireFilters.QUESTION_TYPE) && (
                    <div className={'min-w-60'}>
                        <MultiSelectOutlined
                            options={getQuestionTypeOptions(t).map((option) => ({
                                ...option,
                                label: decodeAndSanitize(getOptionLabelText(option.label)),
                            }))}
                            values={questionTypeFilters}
                            onChange={(value) =>
                                onQuestionTypeFilterChange(value as QuestionTypeValue)
                            }
                            showSelection={true}
                            label={decodeAndSanitize(t('common.questionType'))}
                            sort={'asc'}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
